import { useCallback, useEffect, useState } from 'react';
import { useLevelLadder } from './useLevelLadder';
import { useLevelUpCelebration } from './useLevelUpCelebration';
import { useScoreFactors } from './useScoreFactors';

const isEscapeKey = (event) => event.key === 'Escape';

const createEscapeKeyHandler = (onEscape) => (event) => {
	if (!isEscapeKey(event)) {
		return;
	}

	onEscape();
};

export const useChatLevelProgress = (scoreProgress, { conversationKey } = {}) => {
	const ladder = useLevelLadder(scoreProgress);
	const { factorRows, highestFactorScore } = useScoreFactors(scoreProgress, ladder.t);
	const {
		isLevelUpOpen,
		celebratedLevel,
		celebratedLitCount,
		celebratedColor,
		closeLevelUp,
	} = useLevelUpCelebration(ladder, { conversationKey });
	const [isDetailsOpen, setIsDetailsOpen] = useState(false);
	const isAnyModalOpen = isDetailsOpen || isLevelUpOpen;

	const closeAllModals = useCallback(() => {
		setIsDetailsOpen(false);
		closeLevelUp();
	}, [closeLevelUp]);

	useEffect(() => {
		if (!isLevelUpOpen) {
			return;
		}

		setIsDetailsOpen(false);
	}, [isLevelUpOpen]);

	useEffect(() => {
		if (!isAnyModalOpen) {
			return undefined;
		}

		const handleKeyDown = createEscapeKeyHandler(closeAllModals);

		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [isAnyModalOpen, closeAllModals]);

	const openDetails = () => setIsDetailsOpen(true);
	const closeDetails = () => setIsDetailsOpen(false);

	return {
		t: ladder.t,
		isDetailsOpen,
		isLevelUpOpen,
		celebratedLevel,
		flameLevels: ladder.flameLevels,
		accentColor: ladder.accentColor,
		litCount: ladder.litCount,
		flameCount: ladder.flameCount,
		levelName: ladder.levelName,
		levelDescription: ladder.levelDescription,
		progressPercent: ladder.progressPercent,
		progressLabel: ladder.progressLabel,
		factorRows,
		highestFactorScore,
		celebratedLitCount,
		celebratedColor,
		openDetails,
		closeDetails,
		closeLevelUp,
	};
};
